import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useSession } from '../../stores/session';
import { NewPlaylist } from './LibraryDock';
import { playlists } from './api';

/**
 * Put a track on a playlist, from wherever it is shown.
 *
 * ## Why it is not only in the dock
 *
 * It started in the player bar, on the argument that the thing playing is the
 * thing you want to keep. True, and incomplete: browsing a profile or a chart
 * is exactly when somebody decides a track belongs on a list, and making them
 * play it first to reach the one button that saves it is a detour invented by
 * the software. So the same control sits on the rows too.
 *
 * ## Making one on the way through
 *
 * The first playlist is the one nobody has. If the answer to "add to which?"
 * is "you have none", the menu has to be able to make one and use it in the
 * same gesture - otherwise the first save is: leave here, go to the library,
 * make a list, come back, find the track again.
 */

/** The menu's fixed width, and the room it wants below the trigger. */
const MENU_WIDTH = 260;
const MENU_HEIGHT = 320;
const GAP = 8;
const EDGE = 8;

/**
 * Where the menu goes, given the button it belongs to.
 *
 * ## Down by default, up when there is no room
 *
 * This used to be pinned above the trigger, which was right for the one place
 * it lived: a dock welded to the bottom of the window. On a list row near the
 * top of the page that same rule opens the menu off the top of the screen, so
 * the direction is measured instead. The dock still opens upward, because that
 * is what the measurement says down there.
 */
function place(box: DOMRect) {
  const below = window.innerHeight - box.bottom;
  const above = box.top;

  // Flipped only when down genuinely will not do, and up is better. A menu that
  // changes sides on a few pixels of scroll is worse than a slightly short one.
  const up = below < MENU_HEIGHT + GAP && above > below;

  const left = Math.min(
    Math.max(EDGE, box.left + box.width / 2 - MENU_WIDTH / 2),
    window.innerWidth - MENU_WIDTH - EDGE,
  );

  return {
    position: 'fixed' as const,
    left,
    width: MENU_WIDTH,
    maxHeight: Math.min(MENU_HEIGHT, (up ? above : below) - GAP - EDGE),
    ...(up ? { bottom: window.innerHeight - box.top + GAP } : { top: box.bottom + GAP }),
  };
}

export function AddToPlaylist({
  trackId,
  title,
  size = 'bar',
  className = '',
}: {
  trackId: number;
  title: string;
  /**
   * `bar` is the dock's tight glyph, which has its own spacing around it.
   * `sm` is the 32px box the heart uses, so the two line up in a row.
   */
  size?: 'bar' | 'sm';
  className?: string;
}) {
  const user = useSession((state) => state.user);
  const client = useQueryClient();

  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [added, setAdded] = useState<string | null>(null);
  const [style, setStyle] = useState<ReturnType<typeof place> | null>(null);

  const trigger = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['playlists', user?.username],
    queryFn: () => playlists.mine(user?.username ?? ''),
    enabled: open && Boolean(user),
  });

  const add = useMutation({
    mutationFn: ({ id }: { id: number; name: string }) => playlists.addTrack(id, trackId),
    onSuccess: (_result, variables) => {
      void client.invalidateQueries({ queryKey: ['playlists', user?.username] });
      void client.invalidateQueries({ queryKey: ['playlist', variables.id] });

      setAdded(variables.name);
      setNaming(false);
    },
  });

  /* Measured before paint, so it never appears in the wrong place first. */
  useLayoutEffect(() => {
    if (!open) return;

    const box = trigger.current?.getBoundingClientRect();

    if (box) setStyle(place(box));
  }, [open]);

  /*
   * Followed while it is open.
   *
   * A fixed menu does not move with a scrolling list, so without this it
   * detaches from its row the moment anything scrolls - and these now sit in
   * lists that are almost always scrolling. `Select` does the same, for the
   * same reason.
   */
  useEffect(() => {
    if (!open) return;

    const follow = () => {
      const box = trigger.current?.getBoundingClientRect();

      if (box) setStyle(place(box));
    };

    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);

    return () => {
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [open]);

  /* Escape, and a click anywhere else. See `ChatLauncher` for the pointerdown. */
  useEffect(() => {
    if (!open) return;

    const away = (event: PointerEvent) => {
      const target = event.target as Node;

      // Both refs, because the menu is portalled and is no longer a descendant
      // of the button it belongs to.
      if (trigger.current?.contains(target) || menu.current?.contains(target)) return;

      setOpen(false);
    };

    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);

    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  /*
   * The confirmation clears itself, and clears when the track changes.
   *
   * Otherwise "Added to Late Nights" sits under the next song, claiming
   * something about it that never happened.
   */
  useEffect(() => {
    setAdded(null);
    setNaming(false);
  }, [trackId]);

  useEffect(() => {
    if (added === null) return;

    const timer = setTimeout(() => setAdded(null), 2600);

    return () => clearTimeout(timer);
  }, [added]);

  if (!user) return null;

  const items = data?.items ?? [];

  /*
   * Rendered into the nearest open dialog, the fullscreen element, or the body.
   *
   * Two ways a portal to the body disappears, and both are live here:
   *
   * A modal `<dialog>` makes everything outside it inert, so a menu portalled
   * out of one is painted, in the right place, and completely unclickable - the
   * mistake `Select` documents at length.
   *
   * Native fullscreen renders *only* the fullscreen element and its
   * descendants, so the same portal from inside the now-playing screen is not
   * painted at all. That screen calls `requestFullscreen` on its overlay and
   * has this button on it, so this is the ordinary case rather than an edge.
   *
   * Read on render rather than cached, because the same row appears in a
   * drawer, on a page, and behind a fullscreen overlay.
   */
  const host =
    trigger.current?.closest('dialog') ??
    (typeof document === 'undefined'
      ? null
      : (document.fullscreenElement?.contains(trigger.current) ?? false)
        ? document.fullscreenElement
        : document.body);

  const box =
    size === 'sm'
      ? 'flex h-8 w-8 items-center justify-center rounded-[var(--radius-pill)]'
      : 'rounded-[var(--radius-pill)] px-1.5 py-1';

  return (
    <>
      <button
        ref={trigger}
        type="button"
        onClick={(event) => {
          /*
           * These sit inside rows that are themselves links - the same reason
           * the heart beside them does this. Without it, opening the menu
           * navigates to the track underneath.
           */
          event.preventDefault();
          event.stopPropagation();

          setOpen((was) => !was);
        }}
        aria-expanded={open}
        aria-label={`Add ${title} to a playlist`}
        data-add-to-playlist={trackId}
        className={`shrink-0 transition-colors ${box} ${
          open ? 'text-ink' : 'text-soft hover:text-ink'
        } ${className}`}
      >
        <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden>
          <path d="M7 2h2v5h5v2H9v5H7V9H2V7h5z" />
        </svg>
      </button>

      {open &&
        host !== null &&
        style !== null &&
        createPortal(
          <div
            ref={menu}
            data-add-to-playlist-menu
            style={style}
            /*
              Above `z-50`, which is where the other portalled menus sit, because
              this one can be opened from the now-playing screen - a `z-[60]`
              overlay. Fullscreen usually takes it out of that problem by making
              the overlay the portal host, but fullscreen is refused on iOS, and
              a menu hidden behind the thing that opened it is the worst kind of
              broken: the click worked and nothing appeared.
            */
            className="z-[70] flex flex-col overflow-hidden rounded-[var(--radius-panel)] border border-line-lit bg-raised shadow-2xl"
          >
            <div className="border-b border-line px-3 py-2">
              <p className="text-[13px] font-medium text-ink">Add to playlist</p>
              <p className="truncate text-[11px] text-faint">{title}</p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
              {naming ? (
                <NewPlaylist
                  onDone={(made) => {
                    if (made === null) {
                      setNaming(false);

                      return;
                    }

                    /*
                     * Straight onto the list it just made, which is what "create
                     * one on the fly and then it goes to it" asks for. A new
                     * empty playlist and a track still not saved would be the
                     * gesture half done.
                     */
                    add.mutate({ id: made.id, name: made.title });
                  }}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setNaming(true)}
                  data-add-new-playlist
                  className="flex w-full items-center gap-2 rounded-[var(--radius-control)] px-2 py-2 text-left text-[13px] font-medium text-accent transition-colors hover:bg-slate"
                >
                  <span className="font-mono text-sm">+</span>
                  New playlist
                </button>
              )}

              {isPending && <p className="px-2 py-2 font-mono text-[10px] text-ghost">loading…</p>}

              {items.map((list) => (
                <button
                  key={list.id}
                  type="button"
                  disabled={add.isPending}
                  onClick={() => add.mutate({ id: list.id, name: list.title })}
                  data-add-to={list.id}
                  className="flex w-full items-center gap-2 rounded-[var(--radius-control)] px-2 py-2 text-left transition-colors hover:bg-slate disabled:opacity-50"
                >
                  <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{list.title}</span>
                  <span className="shrink-0 font-mono text-[10px] text-ghost">
                    {list.track_count}
                  </span>
                </button>
              ))}
            </div>

            {(added !== null || add.isError) && (
              <p
                data-add-result
                className={`border-t border-line px-3 py-2 text-[11px] ${
                  add.isError ? 'text-danger' : 'text-soft'
                }`}
              >
                {add.isError ? 'That did not save. Try again.' : `Added to ${added}.`}
              </p>
            )}
          </div>,
          host,
        )}
    </>
  );
}
